import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  PLATFORM_WORKFLOW_STRIP_LABELS,
  PLATFORM_WORKFLOW_STRIP_STEPS,
  type PlatformWorkflowStep,
} from '../../domain/platform/platformOrientationModel';
import { WarehouseCardShell } from '../ui/cards/WarehouseCardShell';

export type PlatformWorkflowStripProps = {
  currentStep: PlatformWorkflowStep;
  secondaryStep?: PlatformWorkflowStep;
  compact?: boolean;
  /** When false, render stages without the outer workflow card. Investigate uses this. */
  framed?: boolean;
  /** Optional click handler — used to jump to the active workflow surface (e.g. Measure → assurance panel). */
  onStepClick?: (step: PlatformWorkflowStep) => void;
  /** Optional label overrides for this surface only. */
  labelOverrides?: Partial<Record<PlatformWorkflowStep, string>>;
};

function isHighlightedStep(
  step: PlatformWorkflowStep,
  currentStep: PlatformWorkflowStep,
  secondaryStep?: PlatformWorkflowStep,
): boolean {
  return step === currentStep || step === secondaryStep;
}

export const PlatformWorkflowStrip: React.FC<PlatformWorkflowStripProps> = ({
  currentStep,
  secondaryStep,
  compact = false,
  framed = true,
  onStepClick,
  labelOverrides,
}) => {
  const theme = useTheme();
  const steps = (
    <Box
      data-testid="platform-workflow-strip-steps"
      sx={{
        display: 'grid',
        gridTemplateColumns: compact
          ? 'repeat(7, minmax(0, 1fr))'
          : { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))', lg: 'repeat(7, minmax(0, 1fr))' },
        gap: compact && !framed ? 0.4 : 0.75,
      }}
    >
      {PLATFORM_WORKFLOW_STRIP_STEPS.map((step) => {
        const highlighted = isHighlightedStep(step, currentStep, secondaryStep);
        const accent = highlighted ? theme.palette.primary.main : theme.palette.text.secondary;
        const clickable = typeof onStepClick === 'function';
        const label = labelOverrides?.[step] || PLATFORM_WORKFLOW_STRIP_LABELS[step];
        return (
          <Box
            key={step}
            component={clickable ? 'button' : 'div'}
            type={clickable ? 'button' : undefined}
            data-testid={`platform-workflow-step-${step}`}
            data-highlighted={highlighted ? 'true' : 'false'}
            onClick={clickable ? () => onStepClick(step) : undefined}
            sx={{
              px: compact ? 0.5 : 0.85,
              py: compact ? 0.4 : 0.75,
              borderRadius: 1.2,
              border: framed || highlighted ? '1px solid' : '1px solid transparent',
              borderColor: highlighted
                ? alpha(accent, 0.28)
                : framed
                  ? alpha(theme.palette.divider, 0.55)
                  : 'transparent',
              bgcolor: highlighted
                ? alpha(accent, 0.08)
                : framed
                  ? alpha(theme.palette.background.default, 0.45)
                  : 'transparent',
              textAlign: 'center',
              cursor: clickable ? 'pointer' : 'default',
              font: 'inherit',
              width: '100%',
              appearance: 'none',
              '&:hover': clickable
                ? {
                    borderColor: alpha(theme.palette.primary.main, 0.45),
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }
                : undefined,
            }}
          >
            <Typography
              variant={compact ? 'caption' : 'body2'}
              sx={{ fontWeight: highlighted ? 800 : 600, color: accent, lineHeight: 1.25 }}
            >
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  if (!framed) {
    return (
      <Box data-testid="platform-workflow-strip" sx={{ mb: compact ? 1.25 : 2 }}>
        {steps}
      </Box>
    );
  }

  return (
    <WarehouseCardShell
      variant="workflowProgress"
      data-testid="platform-workflow-strip"
      sx={{
        px: compact ? 1.1 : { xs: 1.5, md: 2 },
        py: compact ? 1 : 1.35,
        mb: compact ? 1.5 : 2,
        opacity: 0.96,
        borderColor: alpha(theme.palette.divider, 0.5),
        bgcolor: alpha(theme.palette.background.paper, 0.86),
      }}
    >
      {!compact ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.85, fontWeight: 700 }}>
          Platform workflow orientation — not a required sequence for every role
        </Typography>
      ) : null}
      {steps}
    </WarehouseCardShell>
  );
};
